<template>
  <BaseButtonDropdown
    v-model="visible"
    class="tw:text-denim-900"
    new-dropdown
    placement="bottom-start"
  >
    <template #btn="{ attrs, toggle }">
      <MetricWidgetButton
        v-bind="attrs"
        :aria-disabled="!isDropdownVisible"
        :class="{
          soft: dense,
          'tw:flex-col tw:gap-2': stacked,
        }"
        data-testid="sentiment-widget-trigger"
        :read-only="!isDropdownVisible"
        :stacked="stacked"
        @blur="sentimentTooltip?.hide()"
        @click.stop="toggleDropdown(toggle)"
        @focus="sentimentTooltip?.show()"
        @keydown.esc.stop="handleEscape"
      >
        <div
          :class="{
            'tw:m-0 tw:flex tw:size-6 tw:items-center tw:p-0': stacked,
            'tw:mr-0.5 tw:pt-0.5': !stacked && dense,
            'tw:ml-1 tw:mr-2 tw:pt-1': !stacked && !dense,
          }"
        >
          <BaseIcon
            v-if="sentimentOption?.icon"
            :class="sentimentOption.iconClass"
            :icon="sentimentOption.icon"
            :style="{
              'font-size': dense ? '20px' : '18px',
            }"
          />
        </div>

        <div
          class="tw:text-left"
          :class="{
            'tw:text-sm tw:leading-[1.4]': stacked,
            'tw:text-xxs': !stacked && dense,
            'tw:text-base tw:leading-5': !stacked && !dense,
          }"
        >
          {{ getLocaleText("sentiment_widget.heading") }}

          <div
            class="tw:text-left tw:font-bold"
            :class="{
              'tw:text-base': stacked,
              'tw:text-xxs': !stacked && dense,
            }"
          >
            {{ sentimentOption?.abbreviatedLabel }}
          </div>
        </div>

        <BaseStreemTooltip
          v-if="sentimentRatingTooltip"
          ref="sentimentTooltip"
        >
          {{ sentimentRatingTooltip }}
        </BaseStreemTooltip>
      </MetricWidgetButton>
    </template>

    <div
      v-if="isDropdownVisible"
      class="tw:min-w-55 tw:px-4 tw:py-2"
      data-testid="sentiment-widget-menu"
      @click.stop
    >
      <div class="tw:flex tw:items-center tw:justify-between tw:mb-2">
        <div class="tw:text-base tw:font-bold">
          {{ getLocaleText("sentiment_widget.heading") }}
        </div>

        <div class="tw:flex tw:items-center tw:gap-1">
          <!-- Full Media Item Button (For Non-Social Mentions) -->
          <BaseButton
            v-if="
              props.showMediaItemButton &&
              hasEntitySentiment &&
              canOpenInternalLink
            "
            :aria-label="openInternalLinkLabel"
            data-testid="openInternalLink"
            flat
            icon="full-article"
            new-design
            new-icon
            @click="openInternalLink"
          />

          <!-- Close Button -->
          <BaseButton
            :aria-label="getLocaleText('global.close')"
            flat
            icon="close"
            new-design
            new-icon
            @click="visible = false"
          />
        </div>
      </div>
      <div class="tw:border-t tw:border-denim-100 tw:mb-3" />

      <template v-if="allowSentimentRating">
        <SentimentRatingRadio
          v-if="props.forceRadio || !hasEntitySentiment"
          data-testid="sentiment-widget-radio"
          :mention-sentiment-option="mentionSentimentOption"
          :options="sentimentRatingOptions"
          :value="sentimentOption"
          @select="updateSentiment"
        >
          <template
            v-if="sentimentRatingTooltip"
            #helpText
          >
            <div class="tw:text-denim-700 tw:text-xs tw:pt-2">
              {{ sentimentRatingTooltip }}
            </div>
          </template>
        </SentimentRatingRadio>
        <SentimentRatingDropdown
          v-else
          data-testid="sentiment-widget-dropdown-rating"
          :options="sentimentRatingOptions"
          :selected-option="sentimentOption"
          @select="updateSentiment"
        />
      </template>

      <template
        v-if="
          !props.forceRadio &&
          hasEntitySentiment &&
          props.mention.entities_sentiment
        "
      >
        <div
          v-if="allowSentimentRating"
          class="tw:my-3 tw:border-t tw:border-denim-100"
          data-testid="sentiment-widget-divider"
        />

        <div
          class="tw:text-denim-700 tw:text-xs"
          data-testid="sentiment-widget-entity-heading"
        >
          {{
            getLocaleText("entity_sentiment_list.heading", {
              mentionType: mentionType(props.mention),
            })
          }}
        </div>

        <div class="tw:max-h-60 tw:overflow-y-auto">
          <EntitySentimentList :entities="props.mention.entities_sentiment" />
        </div>
      </template>
    </div>
  </BaseButtonDropdown>
</template>

<script setup lang="ts">
import { startCase } from "lodash-es";
import { computed, ref, useTemplateRef } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseButtonDropdown,
  BaseIcon,
  BaseStreemTooltip,
} from "shared/components/base";
import EntitySentimentList from "shared/components/EntitySentimentList.vue";
import MetricWidgetButton from "shared/components/MetricWidgetButton.vue";
import SentimentRatingDropdown from "shared/components/SentimentRatingDropdown.vue";
import SentimentRatingRadio from "shared/components/SentimentRatingRadio.vue";
import useMention from "shared/composables/useMention";
import useMentionSentiment, {
  type Mention,
  type MentionSentimentOption,
} from "shared/composables/useMentionSentiment";
import type { Stream } from "shared/types";

interface SentimentWidgetProps {
  dense?: boolean;
  forceRadio?: boolean;
  mediaRequest?: Record<string, unknown> | null;
  mention: Mention;
  showMediaItemButton?: boolean;
  stacked?: boolean;
  stream: Stream;
}

const props = defineProps<SentimentWidgetProps>();

const {
  allowSentimentRating,
  mentionSentimentOption,
  saveSentimentRating,
  sentimentOption,
  sentimentRatingOptions,
  sentimentRatingTooltip,
} = useMentionSentiment(props);

const { mentionClicked, isMentionNotClickable } = useMention(props);

const visible = ref(false);

const sentimentTooltip =
  useTemplateRef<ComponentExposed<typeof BaseStreemTooltip>>(
    "sentimentTooltip"
  );

const hasEntitySentiment = computed<boolean>(() =>
  Boolean(props.mention.entities_sentiment?.length)
);

const isDropdownVisible = computed<boolean>(() => allowSentimentRating.value);

const archivedContentContext = computed<boolean>(() =>
  Boolean(props.mediaRequest)
);

const openInternalLinkLabel = computed<string>(() => {
  const keySuffix = archivedContentContext.value
    ? "view_archived_media_item"
    : "view_full_media_item";

  return getLocaleText(`actionsheet.${keySuffix}`);
});

const canOpenInternalLink = computed<boolean>(
  () =>
    props.mention.type !== "external_item" &&
    !isMentionNotClickable(props.mention)
);

function toggleDropdown(toggle: () => void): void {
  if (isDropdownVisible.value) toggle();
}

function handleEscape(): void {
  sentimentTooltip.value?.hide();
  visible.value = false;
}

function updateSentiment(option: MentionSentimentOption): void {
  saveSentimentRating(option.value as number);
  visible.value = false;
}

function mentionType(mention: Mention): string {
  return startCase(mention.type ?? "");
}

function openInternalLink(): void {
  visible.value = false;
  mentionClicked(props.mention, true, { defaultTab: "article_sentiment" });
}
</script>
